import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/NotificationBell";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Bus, LogOut, LayoutDashboard, Menu, ClipboardList, ChevronDown, MapPinned, Route as RouteIcon, Package } from "lucide-react";

const NAV_LINKS = [
  { label: "Digital Terminal", href: "/#terminal" },
  { label: "Semua Rute", to: "/rute", testid: "nav-all-routes" },
  { label: "Keunggulan", href: "/#keunggulan" },
  { label: "Blog", to: "/blog" },
  { label: "FAQ", to: "/faq" },
  { label: "Lacak Order", to: "/lacak-order" },
  { label: "Cara Pesan", href: "/#cara" },
];

const SERVICE_LINKS = [
  { label: "Travel Door to Door", to: "/pesan-travel", testid: "nav-pesan-travel", Icon: MapPinned },
  { label: "Charter Mobil", to: "/pesan-charter", testid: "nav-pesan-charter", Icon: RouteIcon },
  { label: "Paket Kilat", to: "/pesan-paket", testid: "nav-pesan-paket", Icon: Package },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const dashPath =
    user?.role === "driver"
      ? "/dashboard-driver"
      : user?.role === "admin" || user?.role === "super_admin"
      ? "/dashboard-admin"
      : null;

  const goOrder = () => {
    if (window.location.pathname === "/") {
      document.getElementById("cari")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/#cari");
    }
  };

  const renderDesktopLink = (l) =>
    l.to ? (
      <Link key={l.label} to={l.to} data-testid={l.testid} className="text-sm text-muted-foreground transition-colors hover:text-foreground">
        {l.label}
      </Link>
    ) : (
      <a key={l.label} href={l.href} className="text-sm text-muted-foreground transition-colors hover:text-foreground">
        {l.label}
      </a>
    );

  const renderDrawerLink = (l) => {
    const cls = "block border-b border-border py-3 text-base text-foreground/90 transition-colors hover:text-primary";
    const testid = `mobile-nav-${slug(l.label)}`;
    return l.to ? (
      <Link key={l.label} to={l.to} data-testid={testid} onClick={() => setOpen(false)} className={cls}>
        {l.label}
      </Link>
    ) : (
      <a key={l.label} href={l.href} data-testid={testid} onClick={() => setOpen(false)} className={cls}>
        {l.label}
      </a>
    );
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-black/60 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src="/travele-wordmark.png" alt="Travele — Digital Terminal" className="h-8 w-auto sm:h-9" />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-pesan-layanan" className="flex items-center gap-1 text-sm font-semibold text-foreground transition-colors hover:text-primary focus:outline-none">
                Pesan Layanan <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {SERVICE_LINKS.map(({ label, to, testid, Icon }) => (
                <DropdownMenuItem key={to} asChild>
                  <Link to={to} data-testid={testid} className="flex cursor-pointer items-center gap-2">
                    <Icon className="h-4 w-4 text-primary" /> {label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {NAV_LINKS.map(renderDesktopLink)}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              {dashPath && <NotificationBell />}
              {!dashPath && (
                <Button data-testid="nav-my-orders-btn" variant="secondary" size="sm" className="hidden lg:inline-flex" onClick={() => navigate("/pesanan-saya")}>
                  <ClipboardList className="mr-1 h-4 w-4" /> Pesanan Saya
                </Button>
              )}
              {dashPath && (
                <Button data-testid="nav-dashboard-btn" variant="secondary" size="sm" className="hidden lg:inline-flex" onClick={() => navigate(dashPath)}>
                  <LayoutDashboard className="mr-1 h-4 w-4" /> Dashboard
                </Button>
              )}
              <Button data-testid="nav-logout-btn" variant="ghost" size="sm" className="hidden lg:inline-flex" onClick={() => { logout(); navigate("/"); }}>
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button data-testid="nav-login-btn" variant="ghost" size="sm" className="hidden lg:inline-flex" onClick={() => navigate("/masuk")}>Masuk Mitra</Button>
              <Button data-testid="nav-order-btn" size="sm" className="tv-cta-glow bg-primary font-semibold text-primary-foreground hover:bg-primary/90" onClick={goOrder}>
                Pesan Sekarang
              </Button>
            </>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="nav-hamburger-btn" variant="ghost" size="icon" className="lg:hidden" aria-label="Buka menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm border-l border-border bg-[#0b0b0d]" data-testid="mobile-nav-drawer">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2 font-display text-lg font-black tracking-tighter">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground"><Bus className="h-4 w-4" /></span>
                  Travele
                </SheetTitle>
              </SheetHeader>
              <nav className="mt-4 flex flex-col">
                <p className="px-0 pb-1 pt-2 text-xs font-semibold uppercase tracking-widest text-primary">Pesan Layanan</p>
                {SERVICE_LINKS.map(({ label, to, testid, Icon }) => (
                  <Link key={to} to={to} data-testid={`mobile-${testid}`} onClick={() => setOpen(false)} className="flex items-center gap-2 border-b border-border py-3 text-base text-foreground/90 transition-colors hover:text-primary">
                    <Icon className="h-4 w-4 text-primary" /> {label}
                  </Link>
                ))}
                {NAV_LINKS.map(renderDrawerLink)}
              </nav>
              <div className="mt-6 flex flex-col gap-2">
                {user ? (
                  <>
                    {!dashPath && (
                      <Button data-testid="mobile-my-orders-btn" variant="secondary" className="w-full justify-start" onClick={() => { setOpen(false); navigate("/pesanan-saya"); }}>
                        <ClipboardList className="mr-2 h-4 w-4" /> Pesanan Saya
                      </Button>
                    )}
                    {dashPath && (
                      <Button data-testid="mobile-dashboard-btn" variant="secondary" className="w-full justify-start" onClick={() => { setOpen(false); navigate(dashPath); }}>
                        <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
                      </Button>
                    )}
                    <Button data-testid="mobile-logout-btn" variant="ghost" className="w-full justify-start" onClick={() => { setOpen(false); logout(); navigate("/"); }}>
                      <LogOut className="mr-2 h-4 w-4" /> Keluar
                    </Button>
                  </>
                ) : (
                  <>
                    <Button data-testid="mobile-login-btn" variant="secondary" className="w-full" onClick={() => { setOpen(false); navigate("/masuk"); }}>Masuk Mitra</Button>
                    <Button data-testid="mobile-order-btn" className="w-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90" onClick={() => { setOpen(false); goOrder(); }}>Pesan Sekarang</Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
